<template>
  <div class="hr-metrics-page">
    <div class="dash-header">
      <div class="dash-header-top">
        <div class="dash-header-left">
          <h1 class="dash-title">外教核心数据看板</h1>
          <p class="dash-sub">
            Foreign Teacher HR Core Metrics Dashboard - 出勤履约 / 人员稳定 / 服务质量 / 成本效益
          </p>
        </div>
        <div class="dash-meta">
          <div class="dash-meta-row" v-for="item in metaRows" :key="item.label">
            <span class="dash-meta-label">{{ item.label }}</span>
            <span class="dash-meta-value">{{ item.value }}</span>
          </div>
        </div>
      </div>
      <div class="dash-chips">
        <span class="dash-chip is-period">
          <i class="chip-icon"></i>
          <span class="chip-label">数据周期</span>
          <Date-picker
            class="period-picker"
            type="daterange"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            v-model="dateRange"
            :clearable="false"
            :disabled="searchLoading"
            transfer
            placeholder="选择周期"
            @on-change="onPeriodChange">
          </Date-picker>
        </span>
        <span class="dash-chip" v-for="chip in infoChips" :key="chip.label">
          <i class="chip-icon"></i>
          <span class="chip-label">{{ chip.label }}</span>
          <span class="chip-value">{{ chip.value }}</span>
        </span>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">核心指标总览</h2>
        <span class="section-legend">
          <i class="legend-dot is-danger"></i>红=预警
          / <i class="legend-dot is-warning"></i>黄=关注
          / <i class="legend-dot is-healthy"></i>绿=健康
        </span>
        <Button class="dash-export" type="primary" :loading="overviewExportLoading" @click="exportOverview">导出</Button>
      </div>
      <div class="section-filters">
        <span class="filter-item">
          <i class="legend-dot is-healthy"></i>健康
        </span>
        <span class="filter-item">
          <i class="legend-dot is-warning"></i>需关注
        </span>
        <span class="filter-item">
          <i class="legend-dot is-danger"></i>预警
        </span>
        <span class="filter-note">※ 同比口径：—</span>
      </div>
      <h3 class="kpi-dim-title">老师维度</h3>
      <div class="kpi-grid">
        <article class="kpi-card is-clickable" :class="leaveRateLevel" @click="openTrend('leaveRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('leaveRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              请假率
            </div>
            <div class="kpi-value">{{ leaveRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ compareText }}</span>
            </div>
            <p class="kpi-foot">已通过请假的老师 + 老师缺席的老师 ÷ 有课老师 · 点击查看日周月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="resignRateLevel" @click="openTrend('resignRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('resignRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              离职率
            </div>
            <div class="kpi-value">{{ resignRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ resignCompareText }}</span>
            </div>
            <p class="kpi-foot">离职人数 ÷ 月初在职人数 · 点击查看日周月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="returnLessonHoursLevel" @click="openTrend('returnLessonHours')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('returnLessonHours')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              返课时数
            </div>
            <div class="kpi-value">{{ returnLessonHoursText }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ returnLessonHoursCompare }}</span>
            </div>
            <p class="kpi-foot">课堂投诉审核通过、外教课、赠课原因是老师 · 点击查看周月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="refundStudentCountLevel" @click="openTrend('refundStudentCount')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('refundStudentCount')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              退费学生数
            </div>
            <div class="kpi-value">{{ refundStudentCountText }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ refundStudentCountCompare }}</span>
            </div>
            <p class="kpi-foot">财务退费列表老师原因，按人去重 · 点击查看月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="demoAttendDealRateLevel" @click="openTrend('demoAttendDealRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('demoAttendDealRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              Demo出席成交率
            </div>
            <div class="kpi-value">{{ demoAttendDealRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ demoAttendDealCompare }}</span>
            </div>
            <p class="kpi-foot">demo成交学生数 ÷ demo出席学生数 · 点击查看月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="fullAttendanceRateLevel" @click="openTrend('fullAttendanceRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('fullAttendanceRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              全勤率
            </div>
            <div class="kpi-value">{{ fullAttendanceRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ fullAttendanceCompare }}</span>
            </div>
            <p class="kpi-foot">无老师迟到/早退/缺席/请假/离职的有课老师 ÷ 有课老师（不含学生请假、管理员取消、学生缺席且老师未缺席的课） · 点击查看日周月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="teacherLateRateLevel" @click="openTrend('teacherLateRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('teacherLateRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              迟到率
            </div>
            <div class="kpi-value">{{ teacherLateRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ teacherLateCompare }}</span>
            </div>
            <p class="kpi-foot">至少有一节迟到的正常上课老师 ÷ 有正常上课的老师。正常上课是已经上完，学生、老师都未缺席；一个老师只计一次 · 点击查看日周月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="teacherEarlyRateLevel" @click="openTrend('teacherEarlyRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('teacherEarlyRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              早退率
            </div>
            <div class="kpi-value">{{ teacherEarlyRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ teacherEarlyCompare }}</span>
            </div>
            <p class="kpi-foot">至少有一节早退的正常上课老师 ÷ 有正常上课的老师。正常上课是已经上完，学生、老师都未缺席；一个老师只计一次 · 点击查看日周月年</p>
          </div>
        </article>
      </div>
      <h3 class="kpi-dim-title">课节维度</h3>
      <div class="kpi-grid">
        <article class="kpi-card is-clickable" :class="attendLessonRateLevel" @click="openTrend('attendLessonRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('attendLessonRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              上课率
            </div>
            <div class="kpi-value">{{ attendLessonRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ attendLessonCompare }}</span>
            </div>
            <p class="kpi-foot">已上课未缺席课节 ÷（买断课节 + 已上课临时课） · 点击查看日周月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="lessonLeaveRateLevel" @click="openTrend('lessonLeaveRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('lessonLeaveRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              请假率
            </div>
            <div class="kpi-value">{{ lessonLeaveRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ lessonLeaveCompare }}</span>
            </div>
            <p class="kpi-foot">全部请假类型或老师缺席 ÷ 应上课课节 · 点击查看日周月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="tempLeaveRateLevel" @click="openTrend('tempLeaveRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('tempLeaveRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              临时请假率
            </div>
            <div class="kpi-value">{{ tempLeaveRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ tempLeaveCompare }}</span>
            </div>
            <p class="kpi-foot">（临时请假 + 失联 + 取消课或老师缺席）÷ 应上课课节 · 点击查看日周月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="lateRateLevel" @click="openTrend('lateRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('lateRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              迟到率
            </div>
            <div class="kpi-value">{{ lateRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ lateCompare }}</span>
            </div>
            <p class="kpi-foot">老师迟到的课 ÷ 正常上课的课（已上完，学生、老师都未缺席；无通话按未缺席未迟到）· 点击查看日周月年</p>
          </div>
        </article>
        <article class="kpi-card is-clickable" :class="earlyRateLevel" @click="openTrend('earlyRate')">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <a class="kpi-gear" href="javascript:;" @click.prevent.stop="openCardThreshold('earlyRate')">
              <Icon type="ios-gear"></Icon>
            </a>
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              早退率
            </div>
            <div class="kpi-value">{{ earlyRate }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ periodText }}</span>
              <span class="kpi-compare">{{ earlyCompare }}</span>
            </div>
            <p class="kpi-foot">老师早退的课 ÷ 正常上课的课（已上完，学生、老师都未缺席；无通话按未缺席未早退）· 点击查看日周月年</p>
          </div>
        </article>
      </div>
    </div>
    <Modal
      v-model="showThresholdModal"
      :title="editCardTitle"
      width="520"
      :mask-closable="false"
      :loading="saveLoading"
      @on-ok="saveThresholds">
      <p class="threshold-tip">{{ editCardHint }}</p>
      <div class="threshold-row">
        <span class="threshold-label">方向</span>
        <RadioGroup v-model="editDirection">
          <Radio label="worse">越小越健康</Radio>
          <Radio label="better">越大越健康</Radio>
        </RadioGroup>
      </div>
      <div class="threshold-row">
        <span class="threshold-field">
          <span class="threshold-label">健康</span>
          <InputNumber v-model="editBand.healthy" :min="0" :max="999999"></InputNumber>
          <span class="threshold-unit">{{ editCardUnit }}</span>
        </span>
        <span class="threshold-field">
          <span class="threshold-label">需关注</span>
          <InputNumber v-model="editBand.attention" :min="0" :max="999999"></InputNumber>
          <span class="threshold-unit">{{ editCardUnit }}</span>
        </span>
        <span class="threshold-field">
          <span class="threshold-label">预警</span>
          <InputNumber v-model="editBand.alert" :min="0" :max="999999"></InputNumber>
          <span class="threshold-unit">{{ editCardUnit }}</span>
        </span>
      </div>
    </Modal>
    <Modal
      v-model="showTrendModal"
      :title="trendModalTitle"
      :width="trendModalWidth"
      :mask-closable="true"
      @on-visible-change="onTrendVisible">
      <div v-if="!showDetail">
        <div class="trend-toolbar">
          <Radio-group v-model="trendPeriodType" type="button" :disabled="trendLoading" @on-change="searchTrend">
            <Radio v-for="item in trendPeriodOptions" :key="item.value" :label="item.value">{{ item.label }}</Radio>
          </Radio-group>
          <Date-picker
            class="trend-picker"
            type="daterange"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            v-model="trendDateRange"
            :clearable="false"
            :disabled="trendLoading"
            transfer
            placeholder="选择周期"
            @on-change="onTrendPeriodChange">
          </Date-picker>
          <div class="detail-adviser trend-adviser">
            <span class="detail-adviser-label">外教运营</span>
            <Select v-model.number="trendStudyAdviserId" placeholder="全部"
                    class="detail-adviser-select"
                    clearable
                    :disabled="trendLoading"
                    @on-change="onTrendStudyAdviserChange">
              <Option v-for="item in studyAdviserList"
                      :value="item.id" :key="item.id">{{ item.nickname }}
              </Option>
            </Select>
          </div>
        </div>
        <p class="trend-tip">{{ trendHint }}</p>
        <Table :columns="trendColumns" :data="trendTableData" :loading="trendLoading" border stripe></Table>
        <div v-if="trendMetric === 'fullAttendanceRate'" class="non-full-block">
          <div class="non-full-head">
            <h4 class="non-full-title">未全勤老师</h4>
            <Input
              v-model="nonFullKeyword"
              class="non-full-search"
              clearable
              placeholder="老师ID / 姓名"
              @on-enter="onNonFullKeywordSearch"
              @on-clear="onNonFullKeywordSearch">
            </Input>
            <Button size="small" type="primary" :loading="nonFullLoading" @click="onNonFullKeywordSearch">搜索</Button>
          </div>
          <p class="trend-tip">该日期范围内有老师责任异常课节的老师。学生请假、管理员取消、学生缺席且老师未迟到早退缺席的课不计入。点异常课节数查看具体课节。</p>
          <Table :columns="nonFullColumns" :data="nonFullTableData" :loading="nonFullLoading" border stripe></Table>
          <div class="trend-page">
            <Page
              :total="nonFullTotal"
              :current="nonFullPageIndex"
              :page-size="nonFullPageSize"
              show-total
              show-sizer
              @on-change="onNonFullPageChange"
              @on-page-size-change="onNonFullPageSizeChange">
            </Page>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="trend-nav">
          <a class="trend-back" href="javascript:;" @click.prevent="backToTrend">返回区间表</a>
          <span class="trend-crumb">{{ detailTitle }}</span>
        </div>
        <p class="trend-tip">{{ detailHint }}</p>
        <div class="detail-adviser">
          <span class="detail-adviser-label">外教运营</span>
          <Select v-model.number="detailStudyAdviserId" placeholder="全部"
                  class="detail-adviser-select"
                  clearable
                  @on-change="onDetailStudyAdviserChange">
            <Option v-for="item in studyAdviserList"
                    :value="item.id" :key="item.id">{{ item.nickname }}
            </Option>
          </Select>
        </div>
        <Table :columns="detailColumns" :data="detailTableData" :loading="detailLoading" border stripe></Table>
        <div class="trend-page">
          <Page
            :total="detailTotal"
            :current="detailPageIndex"
            :page-size="detailPageSize"
            show-total
            show-sizer
            @on-change="onDetailPageChange"
            @on-page-size-change="onDetailPageSizeChange">
          </Page>
        </div>
      </div>
      <div slot="footer">
        <Button :loading="exportLoading" @click="exportCurrent">导出</Button>
        <Button v-if="showDetail" @click="backToTrend">返回</Button>
        <Button type="primary" @click="closeTrendModal">关闭</Button>
      </div>
    </Modal>
    <tch-info-modal :value="showTchInfoModal" @on-close="showTchInfoModal=false" :tchId="tchInfoId"></tch-info-modal>
    <user-info-modal :value="showUserInfoModal" @on-close="showUserInfoModal=false" :uid="userInfoUid"></user-info-modal>
  </div>
</template>

<script>
import TchInfoModal from './modal/TchInfoModal.vue'
import UserInfoModal from '../crm/UserInfoModal.vue'
import { fetchTchScoreMap, applyTchScoresToRows, renderTchNameWithScore } from '@/utils/tchScore'

const DEFAULT_THRESHOLDS = {
  leaveRate: {healthy: 5, attention: 12, alert: 20, higherWorse: true},
  resignRate: {healthy: 2, attention: 5, alert: 8, higherWorse: true},
  returnLessonHours: {healthy: 30, attention: 80, alert: 150, higherWorse: true},
  refundStudentCount: {healthy: 5, attention: 15, alert: 30, higherWorse: true},
  demoAttendDealRate: {healthy: 40, attention: 25, alert: 12, higherWorse: false},
  fullAttendanceRate: {healthy: 90, attention: 80, alert: 70, higherWorse: false},
  teacherLateRate: {healthy: 5, attention: 12, alert: 20, higherWorse: true},
  teacherEarlyRate: {healthy: 5, attention: 12, alert: 20, higherWorse: true},
  attendLessonRate: {healthy: 90, attention: 80, alert: 70, higherWorse: false},
  lessonLeaveRate: {healthy: 5, attention: 12, alert: 20, higherWorse: true},
  tempLeaveRate: {healthy: 5, attention: 12, alert: 20, higherWorse: true},
  lateRate: {healthy: 5, attention: 12, alert: 20, higherWorse: true},
  earlyRate: {healthy: 5, attention: 12, alert: 20, higherWorse: true}
}

function cloneThresholds (src) {
  return JSON.parse(JSON.stringify(src))
}

function fileNameWithRange (name, start, end) {
  let a = start || ''
  let b = end || ''
  let time = a
  if (b && b !== a) {
    time = a + '-' + b
  }
  return name + time + '.xlsx'
}

export default {
  name: 'TchHrMetricsDashboard',
  components: {TchInfoModal, UserInfoModal},
  data () {
    return {
      showTchInfoModal: false,
      rankScoreSeq: 0,
      tchInfoId: null,
      showUserInfoModal: false,
      userInfoUid: null,
      searchLoading: false,
      saveLoading: false,
      overviewExportLoading: false,
      exportLoading: false,
      showThresholdModal: false,
      showTrendModal: false,
      showDetail: false,
      detailLoading: false,
      detailField: '',
      detailPeriod: '',
      detailStartDate: '',
      detailEndDate: '',
      detailTchId: null,
      detailTchName: '',
      detailPageIndex: 1,
      detailPageSize: 20,
      detailTotal: 0,
      detailTableData: [],
      studyAdviserList: [],
      detailStudyAdviserId: null,
      detailAdviserSilent: false,
      trendStudyAdviserId: null,
      trendAdviserSilent: false,
      nonFullLoading: false,
      nonFullKeyword: '',
      nonFullPageIndex: 1,
      nonFullPageSize: 20,
      nonFullTotal: 0,
      nonFullTableData: [],
      trendLoading: false,
      trendMetric: '',
      trendPeriodType: 'day',
      trendDateRange: [],
      trendQuery: {
        startDate: '',
        endDate: ''
      },
      trendTableData: [],
      editKey: '',
      editDirection: 'worse',
      editBand: {healthy: 0, attention: 0, alert: 0},
      thresholds: cloneThresholds(DEFAULT_THRESHOLDS),
      thresholdFields: [
        {key: 'leaveRate', name: '请假率', unit: '%', hint: '已通过请假或老师缺席的老师占比，越低越好。'},
        {key: 'resignRate', name: '离职率', unit: '%', hint: '期内离职人数 ÷ 月初在职人数，越低越好。'},
        {key: 'returnLessonHours', name: '返课时数', unit: '课时', hint: '课堂投诉审核通过、外教课、赠课原因是老师，越少越好。'},
        {key: 'refundStudentCount', name: '退费学生数', unit: '人', hint: '财务退费里老师原因学生数，越少越好。'},
        {key: 'demoAttendDealRate', name: 'Demo出席成交率', unit: '%', hint: '出席试听后成交比例，越高越好。'},
        {key: 'fullAttendanceRate', name: '全勤率', unit: '%', hint: '只看老师责任。学生请假、管理员取消、学生缺席且老师未迟到早退缺席的课不进分子分母。老师请假、离职课算未全勤。'},
        {key: 'teacherLateRate', name: '老师迟到率', unit: '%', hint: '至少有一节迟到的正常上课老师 ÷ 有正常上课的老师，越低越好。'},
        {key: 'teacherEarlyRate', name: '老师早退率', unit: '%', hint: '至少有一节早退的正常上课老师 ÷ 有正常上课的老师，越低越好。'},
        {key: 'attendLessonRate', name: '上课率', unit: '%', hint: '已上课未缺席课节 ÷（买断 + 已上课临时课），越高越好。'},
        {key: 'lessonLeaveRate', name: '课节请假率', unit: '%', hint: '全部请假类型或老师缺席 ÷ 应上课课节，越低越好。'},
        {key: 'tempLeaveRate', name: '临时请假率', unit: '%', hint: '（临时请假 + 失联 + 取消课或老师缺席）÷ 应上课课节，越低越好。'},
        {key: 'lateRate', name: '迟到率', unit: '%', hint: '老师迟到的课 ÷ 正常上课的课，越低越好。'},
        {key: 'earlyRate', name: '早退率', unit: '%', hint: '老师早退的课 ÷ 正常上课的课，越低越好。'}
      ],
      dateRange: [],
      queryParam: {
        startDate: '',
        endDate: ''
      },
      leaveRate: '—',
      leaveTeacherCount: null,
      taughtTeacherCount: null,
      resignRate: '—',
      resignTeacherCount: null,
      resignBaseCount: null,
      returnLessonHours: null,
      refundStudentCount: null,
      demoAttendDealRate: '—',
      demoAttendDealCount: null,
      demoAttendCount: null,
      fullAttendanceRate: '—',
      fullAttendanceCount: null,
      fullAttendanceTotalCount: null,
      teacherLateRate: '—',
      teacherLateCount: null,
      teacherLateBaseCount: null,
      teacherEarlyRate: '—',
      teacherEarlyCount: null,
      teacherEarlyBaseCount: null,
      attendLessonRate: '—',
      attendLessonCount: null,
      attendLessonBaseCount: null,
      lessonLeaveRate: '—',
      lessonLeaveCount: null,
      lessonLeaveBaseCount: null,
      tempLeaveRate: '—',
      tempLeaveCount: null,
      tempLeaveBaseCount: null,
      lateRate: '—',
      lateCount: null,
      lateBaseCount: null,
      earlyRate: '—',
      earlyCount: null,
      earlyBaseCount: null,
      metaRows: [
        {label: '数据来源', value: 'book_lesson_opened / book_calls / book_tch_leave_time / book_course_change'},
        {label: '口径版本', value: 'v1'},
        {label: '最近更新', value: '—'}
      ],
      infoChips: [
        {label: '口径覆盖', value: '已通过请假/老师缺席'},
        {label: '老师请假明细', value: '—'},
        {label: '涉及老师', value: '—'}
      ]
    }
  },
  computed: {
    periodText () {
      if (!this.queryParam.startDate || !this.queryParam.endDate) {
        return '—'
      }
      return this.queryParam.startDate + ' ~ ' + this.queryParam.endDate
    },
    compareText () {
      if (this.leaveTeacherCount == null || this.taughtTeacherCount == null) {
        return '—'
      }
      return this.leaveTeacherCount + ' / ' + this.taughtTeacherCount + ' 人'
    },
    resignCompareText () {
      if (this.resignTeacherCount == null || this.resignBaseCount == null) {
        return '—'
      }
      return this.resignTeacherCount + ' / ' + this.resignBaseCount + ' 人'
    },
    returnLessonHoursText () {
      return this.returnLessonHours == null ? '—' : String(this.returnLessonHours)
    },
    returnLessonHoursCompare () {
      return this.returnLessonHours == null ? '—' : (this.returnLessonHours + ' 课时')
    },
    refundStudentCountText () {
      return this.refundStudentCount == null ? '—' : String(this.refundStudentCount)
    },
    refundStudentCountCompare () {
      return this.refundStudentCount == null ? '—' : (this.refundStudentCount + ' 人')
    },
    demoAttendDealCompare () {
      if (this.demoAttendDealCount == null || this.demoAttendCount == null) {
        return '—'
      }
      return this.demoAttendDealCount + ' / ' + this.demoAttendCount + ' 人'
    },
    fullAttendanceCompare () {
      if (this.fullAttendanceCount == null || this.fullAttendanceTotalCount == null) {
        return '—'
      }
      return this.fullAttendanceCount + ' / ' + this.fullAttendanceTotalCount + ' 人'
    },
    teacherLateCompare () {
      if (this.teacherLateCount == null || this.teacherLateBaseCount == null) {
        return '—'
      }
      return this.teacherLateCount + ' / ' + this.teacherLateBaseCount + ' 人'
    },
    teacherEarlyCompare () {
      if (this.teacherEarlyCount == null || this.teacherEarlyBaseCount == null) {
        return '—'
      }
      return this.teacherEarlyCount + ' / ' + this.teacherEarlyBaseCount + ' 人'
    },
    lateCompare () {
      if (this.lateCount == null || this.lateBaseCount == null) {
        return '—'
      }
      return this.lateCount + ' / ' + this.lateBaseCount + ' 课节'
    },
    earlyCompare () {
      if (this.earlyCount == null || this.earlyBaseCount == null) {
        return '—'
      }
      return this.earlyCount + ' / ' + this.earlyBaseCount + ' 课节'
    },
    leaveRateLevel () {
      return this.levelClass(this.parsePercent(this.leaveRate), this.thresholds.leaveRate)
    },
    resignRateLevel () {
      return this.levelClass(this.parsePercent(this.resignRate), this.thresholds.resignRate)
    },
    returnLessonHoursLevel () {
      return this.levelClass(this.returnLessonHours, this.thresholds.returnLessonHours)
    },
    refundStudentCountLevel () {
      return this.levelClass(this.refundStudentCount, this.thresholds.refundStudentCount)
    },
    demoAttendDealRateLevel () {
      return this.levelClass(this.parsePercent(this.demoAttendDealRate), this.thresholds.demoAttendDealRate)
    },
    fullAttendanceRateLevel () {
      return this.levelClass(this.parsePercent(this.fullAttendanceRate), this.thresholds.fullAttendanceRate)
    },
    teacherLateRateLevel () {
      return this.levelClass(this.parsePercent(this.teacherLateRate), this.thresholds.teacherLateRate)
    },
    teacherEarlyRateLevel () {
      return this.levelClass(this.parsePercent(this.teacherEarlyRate), this.thresholds.teacherEarlyRate)
    },
    lateRateLevel () {
      return this.levelClass(this.parsePercent(this.lateRate), this.thresholds.lateRate)
    },
    earlyRateLevel () {
      return this.levelClass(this.parsePercent(this.earlyRate), this.thresholds.earlyRate)
    },
    attendLessonRateLevel () {
      return this.levelClass(this.parsePercent(this.attendLessonRate), this.thresholds.attendLessonRate)
    },
    lessonLeaveRateLevel () {
      return this.levelClass(this.parsePercent(this.lessonLeaveRate), this.thresholds.lessonLeaveRate)
    },
    tempLeaveRateLevel () {
      return this.levelClass(this.parsePercent(this.tempLeaveRate), this.thresholds.tempLeaveRate)
    },
    attendLessonCompare () {
      if (this.attendLessonCount == null || this.attendLessonBaseCount == null) {
        return '—'
      }
      return this.attendLessonCount + ' / ' + this.attendLessonBaseCount + ' 课节'
    },
    lessonLeaveCompare () {
      if (this.lessonLeaveCount == null || this.lessonLeaveBaseCount == null) {
        return '—'
      }
      return this.lessonLeaveCount + ' / ' + this.lessonLeaveBaseCount + ' 课节'
    },
    tempLeaveCompare () {
      if (this.tempLeaveCount == null || this.tempLeaveBaseCount == null) {
        return '—'
      }
      return this.tempLeaveCount + ' / ' + this.tempLeaveBaseCount + ' 课节'
    },
    editCardMeta () {
      return this.thresholdFields.find(item => item.key === this.editKey) || {}
    },
    editCardTitle () {
      return (this.editCardMeta.name || '指标') + '阈值'
    },
    editCardUnit () {
      return this.editCardMeta.unit || ''
    },
    editCardHint () {
      return this.editCardMeta.hint || '按该指标实际含义设置三个阈值。'
    },
    trendTitle () {
      const names = {
        leaveRate: '请假率',
        resignRate: '离职率',
        fullAttendanceRate: '全勤率',
        teacherLateRate: '老师迟到率',
        teacherEarlyRate: '老师早退率',
        returnLessonHours: '返课时数',
        refundStudentCount: '退费学生数',
        demoAttendDealRate: 'Demo出席成交率',
        attendLessonRate: '上课率',
        lessonLeaveRate: '请假率',
        tempLeaveRate: '临时请假率',
        lateRate: '迟到率',
        earlyRate: '早退率'
      }
      return (names[this.trendMetric] || '指标') + '区间统计'
    },
    trendModalWidth () {
      if (this.showDetail) {
        return 880
      }
      return this.trendMetric === 'fullAttendanceRate' ? 920 : 760
    },
    trendModalTitle () {
      if (this.showDetail) {
        return this.trendTitle + ' › 明细'
      }
      return this.trendTitle
    },
    trendHint () {
      const hints = {
        leaveRate: '已通过请假的老师 + 老师缺席的老师 ÷ 该段有课老师。点分子或分母看该段名单，比率不点。',
        resignRate: '离职人数 ÷ 该段起始日所在月 1 号在职人数。点分子或分母看该段名单，比率不点。',
        fullAttendanceRate: '全勤有课老师 ÷ 该段有课老师。学生请假、管理员取消、学生缺席且老师未迟到早退缺席的课不进分子分母；老师请假、离职课算未全勤。点分子或分母看名单；下方是未全勤老师。',
        teacherLateRate: '老师维度。至少有一节迟到的正常上课老师 ÷ 有正常上课的老师。正常上课是已经上完，学生、老师都未缺席。一个老师只计一次。点分子或分母看名单，比率不点。',
        teacherEarlyRate: '老师维度。至少有一节早退的正常上课老师 ÷ 有正常上课的老师。分母与老师迟到率相同。点分子或分母看名单，比率不点。',
        returnLessonHours: '课堂投诉审核通过、外教课、赠课原因是老师。点数字看该段记录。',
        refundStudentCount: '财务退费老师原因学生数，按人去重。点数字看该段名单。',
        demoAttendDealRate: '段内成交学生数 ÷ 段内出席学生数。点分子或分母看该段名单，比率不点。',
        attendLessonRate: '已上课未缺席课节 ÷（买断课节 + 已上课临时课）。点分子看课节，点分母按老师汇总。',
        lessonLeaveRate: '全部请假类型或老师缺席 ÷ 应上课课节。点分子看课节，点分母按老师汇总。',
        tempLeaveRate: '（临时请假 + 失联 + 取消课或老师缺席）÷ 应上课课节。点分子看课节，点分母按老师汇总。',
        lateRate: '课节维度。老师迟到的课 ÷ 正常上课的课。正常上课是已经上完，学生、老师都未缺席。点分子或分母看课节，比率不点。',
        earlyRate: '课节维度。老师早退的课 ÷ 正常上课的课。分母与迟到率相同。点分子或分母看课节，比率不点。'
      }
      return hints[this.trendMetric] || ''
    },
    trendPeriodOptions () {
      if (this.trendMetric === 'returnLessonHours') {
        return [
          {value: 'week', label: '周'},
          {value: 'month', label: '月'},
          {value: 'year', label: '年'}
        ]
      }
      if (this.trendMetric === 'refundStudentCount' || this.trendMetric === 'demoAttendDealRate') {
        return [
          {value: 'month', label: '月'},
          {value: 'year', label: '年'}
        ]
      }
      return [
        {value: 'day', label: '日'},
        {value: 'week', label: '周'},
        {value: 'month', label: '月'},
        {value: 'year', label: '年'}
      ]
    },
    trendColumns () {
      const self = this
      const numLink = (title, minWidth) => {
        return {
          title: title,
          key: 'numerator',
          align: 'center',
          minWidth: minWidth || 110,
          render: (h, params) => {
            return h('a', {
              class: 'trend-num-link',
              style: {color: '#2d8cf0', cursor: 'pointer'},
              on: {
                click: (e) => {
                  e.stopPropagation()
                  self.openDetail(params.row, 'numerator')
                }
              }
            }, params.row.numerator == null ? '0' : String(params.row.numerator))
          }
        }
      }
      const denLink = (title) => {
        return {
          title: title,
          key: 'denominator',
          align: 'center',
          minWidth: 120,
          render: (h, params) => {
            return h('a', {
              class: 'trend-num-link',
              style: {color: '#2d8cf0', cursor: 'pointer'},
              on: {
                click: (e) => {
                  e.stopPropagation()
                  self.openDetail(params.row, 'denominator')
                }
              }
            }, params.row.denominator == null ? '0' : String(params.row.denominator))
          }
        }
      }
      if (this.trendMetric === 'returnLessonHours') {
        return [
          {title: '周期', key: 'period', align: 'center', minWidth: 160},
          numLink('返课时数', 120)
        ]
      }
      if (this.trendMetric === 'refundStudentCount') {
        return [
          {title: '周期', key: 'period', align: 'center', minWidth: 160},
          numLink('退费学生数', 120)
        ]
      }
      const rateTitle = this.trendTitle.replace('区间统计', '')
      const rateColumn = {
        title: rateTitle,
        key: 'rate',
        align: 'center',
        minWidth: 110
      }
      if (this.trendMetric === 'lessonLeaveRate' || this.trendMetric === 'tempLeaveRate') {
        rateColumn.render = (h, params) => {
          const row = params.row || {}
          return h('span', self.emptyLessonRate(row.rate, row.denominator))
        }
      }
      const numTitle = {
        leaveRate: '请假老师数',
        resignRate: '离职人数',
        fullAttendanceRate: '全勤外教数',
        teacherLateRate: '迟到老师',
        teacherEarlyRate: '早退老师',
        demoAttendDealRate: '成交学生数',
        attendLessonRate: '已上课课节',
        lessonLeaveRate: '全部请假或老师缺席',
        tempLeaveRate: '临时请假+失联+取消课或老师缺席',
        lateRate: '迟到课节',
        earlyRate: '早退课节'
      }[this.trendMetric] || '分子'
      const denTitle = {
        leaveRate: '有课老师数',
        resignRate: '月初在职人数',
        fullAttendanceRate: '有课老师数',
        teacherLateRate: '正常上课老师',
        teacherEarlyRate: '正常上课老师',
        demoAttendDealRate: '出席学生数',
        attendLessonRate: '买断+临时课节',
        lessonLeaveRate: '应上课课节',
        tempLeaveRate: '应上课课节',
        lateRate: '正常上课课节',
        earlyRate: '正常上课课节'
      }[this.trendMetric] || '分母'
      return [
        {title: '周期', key: 'period', align: 'center', minWidth: 160},
        numLink(numTitle, 110),
        denLink(denTitle),
        rateColumn
      ]
    },
    detailTitle () {
      const names = {
        leaveRate: '请假率',
        resignRate: '离职率',
        fullAttendanceRate: '全勤率',
        teacherLateRate: '老师迟到率',
        teacherEarlyRate: '老师早退率',
        returnLessonHours: '返课时数',
        refundStudentCount: '退费学生数',
        demoAttendDealRate: 'Demo出席成交率',
        attendLessonRate: '上课率',
        lessonLeaveRate: '请假率',
        tempLeaveRate: '临时请假率',
        lateRate: '迟到率',
        earlyRate: '早退率'
      }
      const fieldNames = {
        leaveRate: {numerator: '请假老师', denominator: '有课老师'},
        resignRate: {numerator: '离职老师', denominator: '月初在职老师'},
        fullAttendanceRate: {numerator: '全勤外教', denominator: '有课老师', nonFull: '未全勤老师', nonFullLessons: '异常课节'},
        teacherLateRate: {numerator: '迟到老师', denominator: '正常上课老师'},
        teacherEarlyRate: {numerator: '早退老师', denominator: '正常上课老师'},
        returnLessonHours: {numerator: '返课记录'},
        refundStudentCount: {numerator: '退费学生'},
        demoAttendDealRate: {numerator: '成交学生', denominator: '出席学生'},
        attendLessonRate: {numerator: '已上课课节', denominator: '买断+临时'},
        lessonLeaveRate: {numerator: '全部请假或老师缺席', denominator: '应上课课节'},
        tempLeaveRate: {numerator: '临时请假+失联+取消课或老师缺席', denominator: '应上课课节'},
        lateRate: {numerator: '迟到课节', denominator: '正常上课课节'},
        earlyRate: {numerator: '早退课节', denominator: '正常上课课节'}
      }
      const metricName = names[this.trendMetric] || '指标'
      const fieldName = (fieldNames[this.trendMetric] || {})[this.detailField] || '明细'
      const period = this.detailPeriod ? (' · ' + this.detailPeriod) : ''
      const tch = this.detailField === 'nonFullLessons' && this.detailTchName
        ? (' · ' + this.detailTchName)
        : (this.detailField === 'nonFullLessons' && this.detailTchId ? (' · 老师' + this.detailTchId) : '')
      return metricName + ' · ' + fieldName + tch + period
    },
    detailHint () {
      if (this.detailField === 'nonFullLessons') {
        if (!this.detailStartDate || !this.detailEndDate) {
          return '该老师区间内老师责任异常课节：迟到、早退、缺席、请假、离职等。学生请假、管理员取消、学生缺席且老师未缺席的课不计入。'
        }
        return '该老师 ' + this.detailStartDate + ' ~ ' + this.detailEndDate + ' 的异常课节：迟到、早退、缺席、请假、离职等。学生请假、管理员取消、学生缺席且老师未缺席的课不计入。'
      }
      if (!this.detailStartDate || !this.detailEndDate) {
        return '口径与看板该段数字一致。'
      }
      return '口径与看板该段数字一致，日期 ' + this.detailStartDate + ' ~ ' + this.detailEndDate + '。0 也可点，空表即无人。'
    },
    detailColumns () {
      if (this.detailField === 'nonFullLessons') {
        return [
          this.buildTeacherColumn(),
          {title: '课节ID', key: 'lessonId', align: 'center', minWidth: 110},
          {title: '上课时间', key: 'occurTime', align: 'center', minWidth: 170},
          {title: '异常原因', key: 'extra', align: 'center', minWidth: 180}
        ]
      }
      if (this.trendMetric === 'returnLessonHours') {
        return [
          {title: '学生ID', key: 'uid', align: 'center', minWidth: 90},
          {title: '学生姓名', key: 'studentName', align: 'center', minWidth: 120},
          {title: '老师ID', key: 'tchId', align: 'center', minWidth: 90},
          {title: '课时', key: 'num', align: 'center', minWidth: 80},
          {title: '类型', key: 'extra', align: 'center', minWidth: 90},
          {title: '审核时间', key: 'occurTime', align: 'center', minWidth: 160}
        ]
      }
      if (this.trendMetric === 'refundStudentCount') {
        const self = this
        const $constant = this.$constant
        return [
          {
            title: '学生',
            key: 'student',
            align: 'center',
            minWidth: 180,
            render: (h, params) => {
              const row = params.row || {}
              const id = row.uid == null ? '' : String(row.uid)
              const name = (row.studentName || '').trim()
              return h('a', {
                style: {color: '#2d8cf0', cursor: 'pointer'},
                on: {
                  click: () => {
                    if (row.uid) {
                      self.openInfoModal(row.uid)
                    }
                  }
                }
              }, name ? (id + '-' + name) : id)
            }
          },
          {
            title: '最后一节课老师',
            key: 'lastTchName',
            align: 'center',
            minWidth: 200,
            render: (h, params) => {
              const row = params.row || {}
              return renderTchNameWithScore(h, {
                text: (row.lastTchName || '').trim(),
                score: row.monthRankScore
              })
            }
          },
          {title: '对应老师外管', key: 'tchAdviserName', align: 'center', minWidth: 140},
          {
            title: '退费原因分类',
            align: 'center',
            minWidth: 130,
            render: (h, params) => {
              let statusEnum = $constant.getEnum($constant.refundReasonCategory, params.row.refundReasonCategory)
              return h('span', statusEnum && statusEnum.text)
            }
          },
          {title: '退费原因', key: 'refundReason', align: 'center', minWidth: 180},
          {title: '类型', key: 'extra', align: 'center', minWidth: 90},
          {title: '时间', key: 'occurTime', align: 'center', minWidth: 160}
        ]
      }
      if (this.trendMetric === 'demoAttendDealRate') {
        return [
          {title: '学生ID', key: 'uid', align: 'center', minWidth: 90},
          {title: '学生姓名', key: 'studentName', align: 'center', minWidth: 140},
          {title: '成交外教', key: 'demoTch', align: 'center', minWidth: 140},
          {title: '类型', key: 'extra', align: 'center', minWidth: 90},
          {title: '时间', key: 'occurTime', align: 'center', minWidth: 160}
        ]
      }
      if (this.trendMetric === 'lateRate' || this.trendMetric === 'earlyRate') {
        const self = this
        return [
          {title: '课节ID', key: 'lessonId', align: 'center', minWidth: 110},
          {title: '老师ID', key: 'tchId', align: 'center', minWidth: 90},
          {
            title: '姓名',
            key: 'tchName',
            align: 'center',
            minWidth: 140,
            render: (h, params) => {
              let row = params.row
              return renderTchNameWithScore(h, {
                text: self.formatHrTeacherName(row),
                score: row.monthRankScore,
                onClick: row.tchId ? () => self.openTchInfoModal(row.tchId) : null
              })
            }
          },
          {title: '学生ID', key: 'uid', align: 'center', minWidth: 90},
          {title: '上课时间', key: 'occurTime', align: 'center', minWidth: 170},
          {title: '类型', key: 'extra', align: 'center', minWidth: 100}
        ].concat(this.detailField === 'numerator' ? [{
          title: '时长',
          key: 'durationText',
          align: 'center',
          minWidth: 100,
          render: (h, params) => {
            const text = ((params.row && params.row.durationText) || '').trim()
            return h('span', text || '—')
          }
        }] : [])
      }
      if (this.trendMetric === 'attendLessonRate' || this.trendMetric === 'lessonLeaveRate' || this.trendMetric === 'tempLeaveRate') {
        const self = this
        return [
          {title: '老师ID', key: 'tchId', align: 'center', minWidth: 90},
          {
            title: '姓名',
            key: 'tchName',
            align: 'center',
            minWidth: 140,
            render: (h, params) => {
              let row = params.row
              return renderTchNameWithScore(h, {
                text: self.formatHrTeacherName(row),
                score: row.monthRankScore,
                onClick: row.tchId ? () => self.openTchInfoModal(row.tchId) : null
              })
            }
          },
          {title: '课节数', key: 'num', align: 'center', minWidth: 80},
          {title: '类型', key: 'extra', align: 'center', minWidth: 160},
          {title: '时间', key: 'occurTime', align: 'center', minWidth: 160}
        ]
      }
      if (this.trendMetric === 'fullAttendanceRate') {
        return [
          this.buildTeacherColumn(),
          {title: '类型', key: 'extra', align: 'center', minWidth: 120},
          {title: '最后工作日', key: 'lastWorkDay', align: 'center', minWidth: 120},
          {title: '开课时间', key: 'openCourseTime', align: 'center', minWidth: 160}
        ]
      }
      const self = this
      return [
        {title: '老师ID', key: 'tchId', align: 'center', minWidth: 90},
        {
          title: '姓名',
          key: 'tchName',
          align: 'center',
          minWidth: 140,
          render: (h, params) => {
              let row = params.row
              return renderTchNameWithScore(h, {
                text: self.formatHrTeacherName(row),
                score: row.monthRankScore,
                onClick: row.tchId ? () => self.openTchInfoModal(row.tchId) : null
              })
            }
        },
        {title: '类型', key: 'extra', align: 'center', minWidth: 120},
        {title: '最后工作日', key: 'lastWorkDay', align: 'center', minWidth: 120},
        {title: '开课时间', key: 'openCourseTime', align: 'center', minWidth: 160}
      ]
    },
    nonFullColumns () {
      const self = this
      return [
        this.buildTeacherColumn(),
        {
          title: '异常课节数',
          key: 'num',
          align: 'center',
          minWidth: 110,
          render: (h, params) => {
            return h('a', {
              class: 'trend-num-link',
              style: {color: '#2d8cf0', cursor: 'pointer'},
              on: {
                click: (e) => {
                  e.stopPropagation()
                  self.openNonFullLessons(params.row)
                }
              }
            }, params.row.num == null ? '0' : String(params.row.num))
          }
        },
        {title: '类型', key: 'extra', align: 'center', minWidth: 90},
        {title: '最后工作日', key: 'lastWorkDay', align: 'center', minWidth: 120},
        {title: '开课时间', key: 'openCourseTime', align: 'center', minWidth: 160}
      ]
    }
  },
  created () {
    this.thresholds = this.loadThresholds()
    this.loadStudyAdviserList()
  },
  mounted () {
    this.initDefaultDate()
    this.search()
  },
  methods: {
    loadMonthRankScores (listKey) {
      const table = this[listKey]
      if (!table || !table.length) {
        return
      }
      const ids = table.map(function (row) { return row.tchId }).filter(function (id) { return id != null })
      const seq = ++this.rankScoreSeq
      const self = this
      fetchTchScoreMap(this.$http, ids).then(function (map) {
        if (seq !== self.rankScoreSeq) {
          return
        }
        applyTchScoresToRows(self, table, 'tchId', 'monthRankScore', map)
      })
    },

    loadStudyAdviserList () {
      this.$http.post('/adminUser/listByCondition', {adminGroupId: 7, status: 1}).then((res) => {
        if (res && res.status === 'ok' && res.data) {
          this.studyAdviserList = res.data
        }
      })
    },
    positiveAdviserId (value) {
      const id = Number(value)
      if (Number.isInteger(id) && id > 0) {
        return id
      }
      return null
    },
    selectedStudyAdviserId () {
      return this.positiveAdviserId(this.detailStudyAdviserId)
    },
    selectedTrendStudyAdviserId () {
      return this.positiveAdviserId(this.trendStudyAdviserId)
    },
    withStudyAdviser (body, adviserId) {
      const query = body || {}
      if (adviserId) {
        query.studyAdviserId = adviserId
      }
      return query
    },
    formatHrTeacherName (row) {
      const item = row || {}
      const first = (item.firstName || '').trim()
      const last = (item.lastName || '').trim()
      return (first + ' ' + last).trim()
    },
    teacherLabel (row) {
      if (!row) {
        return ''
      }
      const id = row.tchId == null ? '' : String(row.tchId)
      const first = (row.firstName || '').trim()
      const last = (row.lastName || '').trim()
      const name = (first + ' ' + last).trim() || (row.tchName || '').trim()
      return name ? (id + '-' + name) : id
    },
    buildTeacherColumn () {
      const self = this
      return {
        title: '老师',
        key: 'teacher',
        align: 'center',
        minWidth: 240,
        render: (h, params) => {
          const row = params.row || {}
          return renderTchNameWithScore(h, {
            text: self.teacherLabel(row),
            score: row.monthRankScore,
            onClick: row.tchId ? () => self.openTchInfoModal(row.tchId) : null
          })
        }
      }
    },
    openTchInfoModal (tchId) {
      this.tchInfoId = tchId
      this.showTchInfoModal = true
    },
    openInfoModal (uid) {
      this.userInfoUid = uid
      this.showUserInfoModal = true
    },
    emptyLessonRate (rate, base) {
      if (base === 0 || base === '0') {
        return '一'
      }
      if (base == null && (rate == null || rate === '')) {
        return '一'
      }
      return rate || '0%'
    },
    parsePercent (text) {
      if (text == null || text === '—' || text === '') {
        return null
      }
      const num = parseFloat(String(text).replace('%', ''))
      return isNaN(num) ? null : num
    },
    levelClass (value, spec) {
      if (value == null || !spec) {
        return ''
      }
      const healthy = Number(spec.healthy)
      const attention = Number(spec.attention)
      const higherWorse = spec.higherWorse !== false
      if (higherWorse) {
        if (value <= healthy) {
          return 'is-healthy'
        }
        if (value <= attention) {
          return 'is-warning'
        }
        return 'is-danger'
      }
      if (value >= healthy) {
        return 'is-healthy'
      }
      if (value >= attention) {
        return 'is-warning'
      }
      return 'is-danger'
    },
    mergeThresholds (saved) {
      const merged = cloneThresholds(DEFAULT_THRESHOLDS)
      if (!saved) {
        return merged
      }
      Object.keys(merged).forEach((key) => {
        if (!saved[key]) {
          return
        }
        const healthy = Number(saved[key].healthy)
        const attention = Number(saved[key].attention)
        const alert = Number(saved[key].alert)
        if (!isNaN(healthy)) {
          merged[key].healthy = healthy
        }
        if (!isNaN(attention)) {
          merged[key].attention = attention
        }
        if (!isNaN(alert)) {
          merged[key].alert = alert
        }
        if (saved[key].higherWorse === false || saved[key].higherWorse === true) {
          merged[key].higherWorse = saved[key].higherWorse
        }
      })
      return merged
    },
    loadThresholds () {
      return cloneThresholds(DEFAULT_THRESHOLDS)
    },
    openCardThreshold (key) {
      const current = this.thresholds[key] || DEFAULT_THRESHOLDS[key]
      this.editKey = key
      this.editBand = {
        healthy: current.healthy,
        attention: current.attention,
        alert: current.alert
      }
      this.editDirection = current.higherWorse === false ? 'better' : 'worse'
      this.showThresholdModal = true
    },
    saveThresholds () {
      const item = this.editCardMeta
      const healthy = Number(this.editBand.healthy)
      const attention = Number(this.editBand.attention)
      const alert = Number(this.editBand.alert)
      const higherWorse = this.editDirection !== 'better'
      if (!item.key || isNaN(healthy) || isNaN(attention) || isNaN(alert) || healthy < 0 || attention < 0 || alert < 0) {
        this.$Message.error('阈值须为不小于 0 的数字')
        return false
      }
      if (higherWorse && !(healthy < attention && attention < alert)) {
        this.$Message.error('越小越健康时须满足健康 < 需关注 < 预警')
        return false
      }
      if (!higherWorse && !(alert < attention && attention < healthy)) {
        this.$Message.error('越大越健康时须满足预警 < 需关注 < 健康')
        return false
      }
      const next = cloneThresholds(this.thresholds)
      next[item.key] = {healthy: healthy, attention: attention, alert: alert, higherWorse: higherWorse}
      this.saveLoading = true
      this.$http.post('/tch/hr-metrics-threshold', {
        thresholds: JSON.stringify(next)
      }).then((res) => {
        if (res && res.status === 'ok') {
          this.thresholds = next
          this.$Message.success('阈值已保存')
          this.showThresholdModal = false
        } else {
          this.$Message.error((res && res.msg) || '保存失败')
        }
      }).catch(() => {
        this.$Message.error('保存失败')
      }).finally(() => {
        this.saveLoading = false
      })
      return false
    },
    initDefaultDate () {
      const now = this.$moment()
      this.dateRange = [now.clone().startOf('month').format('YYYY-MM-DD'), now.format('YYYY-MM-DD')]
      this.queryParam.startDate = this.dateRange[0]
      this.queryParam.endDate = this.dateRange[1]
    },
    onPeriodChange (value) {
      if (value && value.length === 2 && value[0] && value[1]) {
        this.dateRange = value
        this.queryParam.startDate = value[0]
        this.queryParam.endDate = value[1]
        this.search()
      }
    },
    applyMetrics (data) {
      this.leaveRate = (data && data.leaveRate) ? data.leaveRate : '—'
      this.leaveTeacherCount = data && data.leaveTeacherCount != null ? data.leaveTeacherCount : 0
      this.taughtTeacherCount = data && data.taughtTeacherCount != null ? data.taughtTeacherCount : 0
      this.resignRate = (data && data.resignRate) ? data.resignRate : '0%'
      this.resignTeacherCount = data && data.resignTeacherCount != null ? data.resignTeacherCount : 0
      this.resignBaseCount = data && data.resignBaseCount != null ? data.resignBaseCount : 0
      this.returnLessonHours = data && data.returnLessonHours != null ? data.returnLessonHours : 0
      this.refundStudentCount = data && data.refundStudentCount != null ? data.refundStudentCount : 0
      this.demoAttendDealRate = (data && data.demoAttendDealRate) ? data.demoAttendDealRate : '0%'
      this.demoAttendDealCount = data && data.demoAttendDealCount != null ? data.demoAttendDealCount : 0
      this.demoAttendCount = data && data.demoAttendCount != null ? data.demoAttendCount : 0
      this.fullAttendanceRate = (data && data.fullAttendanceRate) ? data.fullAttendanceRate : '0%'
      this.fullAttendanceCount = data && data.fullAttendanceCount != null ? data.fullAttendanceCount : 0
      this.fullAttendanceTotalCount = data && data.fullAttendanceTotalCount != null ? data.fullAttendanceTotalCount : 0
      this.teacherLateCount = data && data.teacherLateCount != null ? data.teacherLateCount : 0
      this.teacherLateBaseCount = data && data.teacherLateBaseCount != null ? data.teacherLateBaseCount : 0
      this.teacherLateRate = data && data.teacherLateBaseCount === 0 ? '—' : ((data && data.teacherLateRate) ? data.teacherLateRate : '—')
      this.teacherEarlyCount = data && data.teacherEarlyCount != null ? data.teacherEarlyCount : 0
      this.teacherEarlyBaseCount = data && data.teacherEarlyBaseCount != null ? data.teacherEarlyBaseCount : 0
      this.teacherEarlyRate = data && data.teacherEarlyBaseCount === 0 ? '—' : ((data && data.teacherEarlyRate) ? data.teacherEarlyRate : '—')
      this.attendLessonRate = (data && data.attendLessonRate) ? data.attendLessonRate : '0.00%'
      this.attendLessonCount = data && data.attendLessonCount != null ? data.attendLessonCount : 0
      this.attendLessonBaseCount = data && data.attendLessonBaseCount != null ? data.attendLessonBaseCount : 0
      this.lessonLeaveCount = data && data.lessonLeaveCount != null ? data.lessonLeaveCount : 0
      this.lessonLeaveBaseCount = data && data.lessonLeaveBaseCount != null ? data.lessonLeaveBaseCount : 0
      this.lessonLeaveRate = this.emptyLessonRate(data && data.lessonLeaveRate, data && data.lessonLeaveBaseCount)
      this.tempLeaveCount = data && data.tempLeaveCount != null ? data.tempLeaveCount : 0
      this.tempLeaveBaseCount = data && data.tempLeaveBaseCount != null ? data.tempLeaveBaseCount : 0
      this.tempLeaveRate = this.emptyLessonRate(data && data.tempLeaveRate, data && data.tempLeaveBaseCount)
      this.lateCount = data && data.lateCount != null ? data.lateCount : 0
      this.lateBaseCount = data && data.lateBaseCount != null ? data.lateBaseCount : 0
      this.lateRate = data && data.lateBaseCount === 0 ? '—' : ((data && data.lateRate) ? data.lateRate : '—')
      this.earlyCount = data && data.earlyCount != null ? data.earlyCount : 0
      this.earlyBaseCount = data && data.earlyBaseCount != null ? data.earlyBaseCount : 0
      this.earlyRate = data && data.earlyBaseCount === 0 ? '—' : ((data && data.earlyRate) ? data.earlyRate : '—')
      this.thresholds = this.mergeThresholds(data && data.thresholds)
      if (data && data.startDate && data.endDate) {
        this.queryParam.startDate = data.startDate
        this.queryParam.endDate = data.endDate
        this.dateRange = [data.startDate, data.endDate]
      }
      this.infoChips = [
        {label: '口径覆盖', value: '已通过请假/老师缺席'},
        {label: '老师请假明细', value: this.leaveTeacherCount == null ? '—' : String(this.leaveTeacherCount)},
        {label: '涉及老师', value: this.taughtTeacherCount == null ? '—' : String(this.taughtTeacherCount)}
      ]
      this.metaRows = [
        {label: '数据来源', value: 'book_lesson_opened / book_calls / book_tch_leave_time / book_course_change'},
        {label: '口径版本', value: 'v1'},
        {label: '最近更新', value: this.$moment().format('YYYY-MM-DD HH:mm')}
      ]
    },
    resetMetrics () {
      this.leaveRate = '—'
      this.leaveTeacherCount = null
      this.taughtTeacherCount = null
      this.resignRate = '—'
      this.resignTeacherCount = null
      this.resignBaseCount = null
      this.returnLessonHours = null
      this.refundStudentCount = null
      this.demoAttendDealRate = '—'
      this.demoAttendDealCount = null
      this.demoAttendCount = null
      this.fullAttendanceRate = '—'
      this.fullAttendanceCount = null
      this.fullAttendanceTotalCount = null
      this.teacherLateRate = '—'
      this.teacherLateCount = null
      this.teacherLateBaseCount = null
      this.teacherEarlyRate = '—'
      this.teacherEarlyCount = null
      this.teacherEarlyBaseCount = null
      this.attendLessonRate = '—'
      this.attendLessonCount = null
      this.attendLessonBaseCount = null
      this.lessonLeaveRate = '—'
      this.lessonLeaveCount = null
      this.lessonLeaveBaseCount = null
      this.tempLeaveRate = '—'
      this.tempLeaveCount = null
      this.tempLeaveBaseCount = null
      this.lateRate = '—'
      this.lateCount = null
      this.lateBaseCount = null
      this.earlyRate = '—'
      this.earlyCount = null
      this.earlyBaseCount = null
      this.infoChips = [
        {label: '口径覆盖', value: '已通过请假/老师缺席'},
        {label: '老师请假明细', value: '—'},
        {label: '涉及老师', value: '—'}
      ]
    },
    openTrend (metric) {
      const defaultPeriod = {
        leaveRate: 'day',
        resignRate: 'day',
        fullAttendanceRate: 'day',
        teacherLateRate: 'day',
        teacherEarlyRate: 'day',
        returnLessonHours: 'week',
        refundStudentCount: 'month',
        demoAttendDealRate: 'month',
        attendLessonRate: 'day',
        lessonLeaveRate: 'day',
        tempLeaveRate: 'day',
        lateRate: 'day',
        earlyRate: 'day'
      }
      this.trendMetric = metric
      this.trendPeriodType = defaultPeriod[metric] || 'day'
      this.trendQuery.startDate = this.queryParam.startDate
      this.trendQuery.endDate = this.queryParam.endDate
      this.trendDateRange = [this.queryParam.startDate, this.queryParam.endDate]
      this.trendAdviserSilent = true
      this.trendStudyAdviserId = null
      this.$nextTick(() => {
        this.trendAdviserSilent = false
      })
      this.resetDetail()
      this.showTrendModal = true
      this.searchTrend()
    },
    closeTrendModal () {
      this.showTrendModal = false
      this.resetDetail()
    },
    onTrendVisible (visible) {
      if (!visible) {
        this.resetDetail()
      }
    },
    resetDetail () {
      this.showDetail = false
      this.detailField = ''
      this.detailPeriod = ''
      this.detailStartDate = ''
      this.detailEndDate = ''
      this.detailTchId = null
      this.detailTchName = ''
      this.detailPageIndex = 1
      this.detailPageSize = 20
      this.detailTotal = 0
      this.detailTableData = []
      this.nonFullKeyword = ''
      this.nonFullPageIndex = 1
      this.nonFullPageSize = 20
      this.nonFullTotal = 0
      this.nonFullTableData = []
    },
    backToTrend () {
      this.showDetail = false
      this.detailField = ''
      this.detailPeriod = ''
      this.detailStartDate = ''
      this.detailEndDate = ''
      this.detailTchId = null
      this.detailTchName = ''
      this.detailTableData = []
      this.detailTotal = 0
      this.detailPageIndex = 1
    },
    onTrendPeriodChange (value) {
      if (value && value.length === 2 && value[0] && value[1]) {
        this.trendDateRange = value
        this.trendQuery.startDate = value[0]
        this.trendQuery.endDate = value[1]
        this.searchTrend()
      }
    },
    searchTrend () {
      if (!this.trendMetric || !this.trendQuery.startDate || !this.trendQuery.endDate) {
        this.trendTableData = []
        return
      }
      this.trendLoading = true
      this.$http.post('/tch/hr-metrics-trend', this.withStudyAdviser({
        metric: this.trendMetric,
        periodType: this.trendPeriodType,
        startDate: this.trendQuery.startDate,
        endDate: this.trendQuery.endDate
      }, this.selectedTrendStudyAdviserId())).then((res) => {
        this.trendTableData = res && res.status === 'ok' && Array.isArray(res.data) ? res.data : []
      }).catch(() => {
        this.trendTableData = []
      }).finally(() => {
        this.trendLoading = false
      })
      if (this.trendMetric === 'fullAttendanceRate') {
        this.nonFullPageIndex = 1
        this.searchNonFull()
      } else {
        this.nonFullTableData = []
        this.nonFullTotal = 0
      }
    },
    searchNonFull () {
      if (this.trendMetric !== 'fullAttendanceRate' || !this.trendQuery.startDate || !this.trendQuery.endDate) {
        this.nonFullTableData = []
        this.nonFullTotal = 0
        return
      }
      this.nonFullLoading = true
      const nonFullQuery = {
        metric: 'fullAttendanceRate',
        field: 'nonFull',
        startDate: this.trendQuery.startDate,
        endDate: this.trendQuery.endDate,
        pageIndex: this.nonFullPageIndex,
        pageSize: this.nonFullPageSize
      }
      const kw = (this.nonFullKeyword || '').trim()
      if (kw) {
        nonFullQuery.keyword = kw
      }
      this.$http.post('/tch/hr-metrics-trend-detail', this.withStudyAdviser(
        nonFullQuery, this.selectedTrendStudyAdviserId())).then((res) => {
        if (res && res.status === 'ok' && res.data) {
          this.nonFullTableData = res.data.data || []
          this.loadMonthRankScores('nonFullTableData')
          this.nonFullTotal = res.data.totalCount || 0
        } else {
          this.nonFullTableData = []
          this.nonFullTotal = 0
          this.$Message.error((res && res.msg) || '未全勤老师加载失败')
        }
      }).catch(() => {
        this.nonFullTableData = []
        this.nonFullTotal = 0
        this.$Message.error('未全勤老师加载失败')
      }).finally(() => {
        this.nonFullLoading = false
      })
    },
    onNonFullPageChange (page) {
      this.nonFullPageIndex = page
      this.searchNonFull()
    },
    onNonFullPageSizeChange (size) {
      this.nonFullPageSize = size
      this.nonFullPageIndex = 1
      this.searchNonFull()
    },
    onNonFullKeywordSearch () {
      this.nonFullPageIndex = 1
      this.searchNonFull()
    },
    openNonFullLessons (row) {
      if (!row || !row.tchId) {
        return
      }
      this.detailField = 'nonFullLessons'
      this.detailPeriod = (this.trendQuery.startDate || '') + ' ~ ' + (this.trendQuery.endDate || '')
      this.detailStartDate = this.trendQuery.startDate
      this.detailEndDate = this.trendQuery.endDate
      this.detailTchId = row.tchId
      this.detailTchName = ((row.firstName || '') + ' ' + (row.lastName || '')).trim() || String(row.tchId)
      this.detailPageIndex = 1
      this.detailPageSize = 20
      this.detailTableData = []
      this.detailTotal = 0
      this.carryTrendAdviserToDetail()
      this.showDetail = true
      this.searchDetail()
    },
    openDetail (row, field) {
      if (!row || !this.trendMetric || !field) {
        return
      }
      this.detailField = field
      this.detailPeriod = row.period || ''
      this.detailStartDate = row.startDate
      this.detailEndDate = row.endDate
      this.detailTchId = null
      this.detailTchName = ''
      this.detailPageIndex = 1
      this.detailPageSize = 20
      this.detailTableData = []
      this.detailTotal = 0
      this.carryTrendAdviserToDetail()
      this.showDetail = true
      this.searchDetail()
    },
    resetDetailStudyAdviser () {
      this.detailAdviserSilent = true
      this.detailStudyAdviserId = null
      this.$nextTick(() => {
        this.detailAdviserSilent = false
      })
    },
    carryTrendAdviserToDetail () {
      this.detailAdviserSilent = true
      this.detailStudyAdviserId = this.selectedTrendStudyAdviserId()
      this.$nextTick(() => {
        this.detailAdviserSilent = false
      })
    },
    onTrendStudyAdviserChange () {
      if (this.trendAdviserSilent) {
        return
      }
      this.searchTrend()
    },
    onDetailStudyAdviserChange () {
      if (this.detailAdviserSilent) {
        return
      }
      this.detailPageIndex = 1
      this.searchDetail()
    },
    onDetailPageChange (page) {
      this.detailPageIndex = page
      this.searchDetail()
    },
    onDetailPageSizeChange (size) {
      this.detailPageSize = size
      this.detailPageIndex = 1
      this.searchDetail()
    },
    searchDetail () {
      if (!this.trendMetric || !this.detailField || !this.detailStartDate || !this.detailEndDate) {
        this.detailTableData = []
        this.detailTotal = 0
        return
      }
      this.detailLoading = true
      const detailQuery = {
        metric: this.trendMetric,
        field: this.detailField,
        startDate: this.detailStartDate,
        endDate: this.detailEndDate,
        tchId: this.detailTchId,
        pageIndex: this.detailPageIndex,
        pageSize: this.detailPageSize
      }
      const studyAdviserId = this.selectedStudyAdviserId()
      if (studyAdviserId) {
        detailQuery.studyAdviserId = studyAdviserId
      }
      this.$http.post('/tch/hr-metrics-trend-detail', detailQuery).then((res) => {
        if (res && res.status === 'ok' && res.data) {
          this.detailTableData = res.data.data || []
          this.loadMonthRankScores('detailTableData')
          this.detailTotal = res.data.totalCount || 0
        } else {
          this.detailTableData = []
          this.detailTotal = 0
          this.$Message.error((res && res.msg) || '明细加载失败')
        }
      }).catch(() => {
        this.detailTableData = []
        this.detailTotal = 0
        this.$Message.error('明细加载失败')
      }).finally(() => {
        this.detailLoading = false
      })
    },
    search () {
      this.searchLoading = true
      this.$http.post('/tch/hr-metrics', {
        startDate: this.queryParam.startDate,
        endDate: this.queryParam.endDate
      }).then((res) => {
        if (res && res.status === 'ok') {
          this.applyMetrics(res.data || {})
        } else {
          this.resetMetrics()
        }
      }).catch(() => {
        this.resetMetrics()
      }).finally(() => {
        this.searchLoading = false
      })
    },
    exportOverview () {
      this.overviewExportLoading = true
      this.downloadExcel('/tch/hr-metrics-export', {
        startDate: this.queryParam.startDate,
        endDate: this.queryParam.endDate
      }, fileNameWithRange('人事看板', this.queryParam.startDate, this.queryParam.endDate)).finally(() => {
        this.overviewExportLoading = false
      })
    },
    exportCurrent () {
      if (this.showDetail) {
        this.exportDetail()
      } else {
        this.exportTrend()
      }
    },
    exportTrend () {
      const names = {
        leaveRate: '请假率区间',
        resignRate: '离职率区间',
        fullAttendanceRate: '全勤率区间',
        teacherLateRate: '老师迟到率区间',
        teacherEarlyRate: '老师早退率区间',
        returnLessonHours: '返课时数区间',
        refundStudentCount: '退费学生数区间',
        demoAttendDealRate: 'Demo出席成交率区间',
        attendLessonRate: '上课率区间',
        lessonLeaveRate: '课节请假率区间',
        tempLeaveRate: '临时请假率区间',
        lateRate: '迟到率区间',
        earlyRate: '早退率区间'
      }
      this.exportLoading = true
      this.downloadExcel('/tch/hr-metrics-trend-export', this.withStudyAdviser({
        metric: this.trendMetric,
        periodType: this.trendPeriodType,
        startDate: this.trendQuery.startDate,
        endDate: this.trendQuery.endDate
      }, this.selectedTrendStudyAdviserId()), fileNameWithRange(names[this.trendMetric] || '人事看板区间', this.trendQuery.startDate, this.trendQuery.endDate)).finally(() => {
        this.exportLoading = false
      })
    },
    exportDetail () {
      const names = {
        leaveRate: {numerator: '请假老师明细', denominator: '有课老师明细'},
        resignRate: {numerator: '离职老师明细', denominator: '月初在职老师明细'},
        fullAttendanceRate: {numerator: '全勤外教明细', denominator: '有课老师明细', nonFull: '未全勤老师明细', nonFullLessons: '异常课节明细'},
        teacherLateRate: {numerator: '迟到老师明细', denominator: '正常上课老师明细'},
        teacherEarlyRate: {numerator: '早退老师明细', denominator: '正常上课老师明细'},
        returnLessonHours: {numerator: '返课记录明细'},
        refundStudentCount: {numerator: '退费学生明细'},
        demoAttendDealRate: {numerator: '成交学生明细', denominator: '出席学生明细'},
        attendLessonRate: {numerator: '已上课课节明细', denominator: '买断临时课节明细'},
        lessonLeaveRate: {numerator: '全部请假或老师缺席明细', denominator: '应上课课节明细'},
        tempLeaveRate: {numerator: '临时请假失联取消课或缺席明细', denominator: '应上课课节明细'},
        lateRate: {numerator: '迟到课节明细', denominator: '正常上课课节明细'},
        earlyRate: {numerator: '早退课节明细', denominator: '正常上课课节明细'}
      }
      const file = fileNameWithRange((names[this.trendMetric] || {})[this.detailField] || '人事看板明细', this.detailStartDate, this.detailEndDate)
      const detailQuery = {
        metric: this.trendMetric,
        field: this.detailField,
        startDate: this.detailStartDate,
        endDate: this.detailEndDate,
        tchId: this.detailTchId
      }
      const studyAdviserId = this.selectedStudyAdviserId()
      if (studyAdviserId) {
        detailQuery.studyAdviserId = studyAdviserId
      }
      if ((this.trendMetric === 'lateRate' || this.trendMetric === 'earlyRate') && this.detailField === 'numerator') {
        detailQuery.withDuration = 1
      }
      this.exportLoading = true
      this.downloadExcel('/tch/hr-metrics-trend-detail-export', detailQuery, file).finally(() => {
        this.exportLoading = false
      })
    },
    downloadExcel (url, data, filename) {
      return this.$http({
        method: 'post',
        url: url,
        data: data,
        responseType: 'arraybuffer'
      }).then((response) => {
        if (response && response.data) {
          const blob = new Blob([response.data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
          const link = document.createElement('a')
          link.target = '_blank'
          link.href = window.URL.createObjectURL(blob)
          link.download = filename
          link.click()
          window.URL.revokeObjectURL(link.href)
        } else {
          this.$Message.error('导出失败')
        }
      }).catch(() => {
        this.$Message.error('导出失败')
      })
    }
  }
}
</script>

<style scoped lang="less">
@page-bg: #F5F6FA;
@header-bg: #2A3C62;
@header-bg-end: #314E78;
@text: #1A1A1A;
@text-title: #303133;
@text-muted: #8B93A7;
@text-desc: #9AA3B2;
@card: #FFFFFF;
@danger: #E6464E;
@warning: #E89F2D;
@healthy: #0B9E8D;
@radius-header: 16px;
@radius-card: 12px;
@gap: 16px;

.hr-metrics-page {
  margin: -24px;
  min-height: calc(100vh - 108px);
  padding: 16px;
  background: @page-bg;
}

.dash-header {
  padding: 20px 24px 16px;
  border-radius: @radius-header;
  background: linear-gradient(90deg, @header-bg 0%, @header-bg-end 100%);
  color: #fff;
}

.dash-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: @gap;
}

.dash-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: #fff;
}

.dash-sub {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.7);
}

.dash-meta {
  text-align: right;
  white-space: nowrap;
}

.dash-meta-row {
  font-size: 12px;
  line-height: 1.7;
}

.dash-meta-label {
  margin-right: 8px;
  color: rgba(255, 255, 255, 0.55);
}

.dash-meta-value {
  color: rgba(255, 255, 255, 0.92);
}

.dash-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.dash-chip {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.88);
}

.chip-icon {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 6px;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 50%;
}

.chip-label {
  margin-right: 6px;
}

.chip-value {
  font-weight: 600;
}

.dash-chip.is-period {
  padding-right: 8px;
}

.period-picker {
  width: 210px;
}

.dash-chip /deep/ .ivu-input {
  height: 24px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  box-shadow: none;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.dash-chip /deep/ .ivu-input:hover,
.dash-chip /deep/ .ivu-input:focus {
  border: 0;
  box-shadow: none;
}

.dash-chip /deep/ .ivu-input::-webkit-input-placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.dash-chip /deep/ .ivu-date-picker-rel {
  line-height: 24px;
}

.dash-chip /deep/ .ivu-icon {
  color: rgba(255, 255, 255, 0.7);
}

.dash-section {
  margin-top: 18px;
}

.section-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dash-export {
  margin-left: auto;
}

.threshold-tip {
  margin: 0 0 16px;
  font-size: 12px;
  color: @text-muted;
}

.threshold-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.threshold-name {
  width: 130px;
  font-size: 13px;
  color: @text-title;
}

.threshold-dir {
  width: 72px;
  font-size: 12px;
  color: @text-muted;
}

.threshold-field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.threshold-label {
  font-size: 12px;
  color: @text-muted;
}

.threshold-unit {
  width: 28px;
  font-size: 12px;
  color: @text-desc;
}

.threshold-row /deep/ .ivu-input-number {
  width: 88px;
}

.section-title {
  margin: 0;
  padding-left: 10px;
  border-left: 4px solid @header-bg;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: @text;
}

.section-legend {
  font-size: 12px;
  color: @text-muted;
}

.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 4px 0 8px;
  border-radius: 50%;
  vertical-align: 1px;
}

.legend-dot.is-danger {
  background: @danger;
}

.legend-dot.is-warning {
  background: @warning;
}

.legend-dot.is-healthy {
  background: @healthy;
}

.section-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
  font-size: 13px;
  color: @text-title;
}

.filter-item {
  display: inline-flex;
  align-items: center;
}

.filter-item .legend-dot {
  margin-left: 0;
  margin-right: 6px;
}

.filter-note {
  margin-left: auto;
  font-size: 12px;
  color: @text-muted;
}

.kpi-dim-title {
  margin: 18px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: @gap;
  margin-top: 16px;
}

.kpi-card {
  position: relative;
  display: flex;
  min-height: 0;
  background: @card;
  border-radius: @radius-card;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px rgba(15, 23, 42, .04);
  overflow: hidden;
}

.kpi-card.is-clickable {
  cursor: pointer;
}

.trend-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.trend-picker {
  width: 240px;
}

.trend-toolbar .trend-adviser {
  margin-bottom: 0;
}

.trend-tip {
  margin: 0 0 12px;
  font-size: 12px;
  color: @text-muted;
}

.detail-adviser {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.detail-adviser-label {
  margin-right: 8px;
  font-size: 13px;
  color: @text-title;
  flex: none;
}

.detail-adviser-select {
  width: 180px;
}

.trend-nav {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.trend-back {
  color: #2d8cf0;
  font-size: 13px;
}

.trend-crumb {
  font-size: 13px;
  color: @text-title;
}

.trend-page {
  margin-top: 12px;
  text-align: right;
}

.non-full-block {
  margin-top: 20px;
}

.non-full-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.non-full-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: @text-title;
}

.non-full-search {
  width: 200px;
}

.trend-num-link {
  color: #2d8cf0;
  cursor: pointer;
}

.kpi-gear {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 1;
  color: #9AA3B2;
  font-size: 16px;
  line-height: 1;
}

.kpi-gear:hover {
  color: #314E78;
}

.kpi-bar {
  width: 4px;
  flex-shrink: 0;
  background: #D8DEE8;
}

.kpi-card.is-danger .kpi-bar,
.kpi-card.is-danger .kpi-dot {
  background: @danger;
}

.kpi-card.is-warning .kpi-bar,
.kpi-card.is-warning .kpi-dot {
  background: @warning;
}

.kpi-card.is-healthy .kpi-bar,
.kpi-card.is-healthy .kpi-dot {
  background: @healthy;
}

.kpi-body {
  flex: 1;
  min-width: 0;
  padding: 16px 18px 14px;
}

.kpi-title {
  display: flex;
  align-items: center;
  padding-right: 22px;
  font-size: 13px;
  line-height: 1.4;
  color: @text-title;
  word-break: break-word;
}

.kpi-dot {
  display: inline-block;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
}

.kpi-value {
  margin-top: 18px;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.2;
  color: @text;
  word-break: break-word;
}

.kpi-period-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.5;
  color: @text-muted;
}

.kpi-period {
  min-width: 0;
  flex: 1 1 auto;
}

.kpi-compare {
  flex-shrink: 0;
  color: @danger;
  font-weight: 600;
  white-space: nowrap;
}

.kpi-foot {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #EEF0F4;
  font-size: 12px;
  line-height: 1.5;
  color: @text-desc;
  word-break: break-word;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .hr-metrics-page {
    margin: -16px;
    padding: 12px;
  }

  .dash-header-top {
    flex-direction: column;
  }

  .dash-meta {
    text-align: left;
    white-space: normal;
  }

  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .kpi-body {
    padding: 14px 14px 12px;
  }

  .kpi-value {
    margin-top: 10px;
    font-size: 28px;
  }
}
</style>



// WEBPACK FOOTER //
// TchHrMetricsDashboard.vue?0da837b0